import React, { useState, useEffect } from 'react';
import './FAQ.css';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

const FAQ = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const sections = [
    { id: 'getting-started', title: 'Getting Started' },
    { id: 'account', title: 'Account & Profile' },
    { id: 'messaging', title: 'Messaging & Chat' },
    { id: 'privacy-security', title: 'Privacy & Security' },
    { id: 'troubleshooting', title: 'Troubleshooting' },
    { id: 'billing', title: 'Billing & Plans' },
    { id: 'technical', title: 'Technical Support' }
  ];

  const faqData = {
    'getting-started': [
      {
        question: "What is WeMeetTo?",
        answer: "WeMeetTo is a modern messaging platform designed to connect people and facilitate meaningful interactions. We provide real-time chat, file sharing, group conversations, and more in a secure and beautiful environment."
      },
      {
        question: "How do I get started with WeMeetTo?",
        answer: "Getting started is easy! Simply visit our website, click 'Get Started', create your account with your email address, and you'll be ready to start connecting with others. You can also download our mobile app for on-the-go messaging."
      },
      {
        question: "Is WeMeetTo free to use?",
        answer: "Yes! WeMeetTo offers a free tier with essential features including messaging, file sharing, and group chats. We also offer premium plans with advanced features for users who need more functionality."
      },
      {
        question: "What platforms does WeMeetTo support?",
        answer: "WeMeetTo is available on multiple platforms including web browsers, iOS, Android, Windows, and macOS. Your conversations sync seamlessly across all your devices."
      }
    ],
    'account': [
      {
        question: "How do I create an account?",
        answer: "Creating an account is simple. Click the 'Sign Up' button, enter your email address, create a secure password, and verify your email. You can also sign up using your Google or Apple account for faster registration."
      },
      {
        question: "Can I change my username or email?",
        answer: "Yes, you can change your username anytime in your profile settings. To change your email address, go to Account Settings > Email and follow the verification process. Note that changing your email will require re-verification."
      },
      {
        question: "How do I delete my account?",
        answer: "To delete your account, go to Account Settings > Privacy & Security > Delete Account. This action is permanent and will remove all your data, messages, and files. Make sure to download any important data before proceeding."
      },
      {
        question: "What happens to my data if I delete my account?",
        answer: "When you delete your account, all your personal data, messages, files, and account information are permanently removed from our servers within 30 days. This action cannot be undone."
      }
    ],
    'messaging': [
      {
        question: "How do I start a conversation?",
        answer: "To start a conversation, click the 'New Chat' button, search for the person you want to message by their username or email, and click on their profile to start chatting. You can also create group chats by selecting multiple contacts."
      },
      {
        question: "Can I send files and media?",
        answer: "Absolutely! You can send various file types including images, videos, documents, and more. Simply click the attachment icon in the chat, select your file, and send. We support files up to 100MB in size."
      },
      {
        question: "How do group chats work?",
        answer: "Group chats allow you to communicate with multiple people at once. You can create groups, add or remove members, set group names and photos, and use features like mentions and reactions. Group admins have additional management options."
      },
      {
        question: "Can I edit or delete messages?",
        answer: "Yes, you can edit messages within 15 minutes of sending them, and delete messages at any time. Edited messages will show an 'edited' indicator, and deleted messages will be removed for all participants."
      },
      {
        question: "How do I use voice messages?",
        answer: "To send a voice message, hold down the microphone button in the chat, speak your message, and release the button to send. Voice messages are automatically transcribed for accessibility and can be played back by recipients."
      }
    ],
    'privacy-security': [
      {
        question: "Is my data secure on WeMeetTo?",
        answer: "Yes, we take security seriously. All messages are encrypted end-to-end, and we use industry-standard security measures to protect your data. We never share your personal information with third parties without your consent."
      },
      {
        question: "Can I make my profile private?",
        answer: "Yes, you can control your privacy settings in Account Settings > Privacy. You can choose who can find you, send you messages, see your online status, and more. You have full control over your privacy."
      },
      {
        question: "How does end-to-end encryption work?",
        answer: "End-to-end encryption ensures that only you and the person you're messaging can read your messages. Even we cannot access the content of your encrypted messages. Your conversations are protected with military-grade encryption."
      },
      {
        question: "Can I block or report users?",
        answer: "Yes, you can block users to prevent them from contacting you, and report users for inappropriate behavior. Blocked users cannot see your online status or send you messages. Reports are reviewed by our moderation team."
      }
    ],
    'troubleshooting': [
      {
        question: "I can't receive messages. What should I do?",
        answer: "First, check your internet connection and try refreshing the page or restarting the app. Make sure you're not in Do Not Disturb mode. If the issue persists, try logging out and back in, or contact our support team."
      },
      {
        question: "Why are my messages not sending?",
        answer: "This could be due to a poor internet connection, server maintenance, or app issues. Try checking your connection, restarting the app, or clearing your browser cache. If problems continue, contact support."
      },
      {
        question: "I'm having trouble with file uploads. What's wrong?",
        answer: "File upload issues can be caused by large file sizes (over 100MB), unsupported file types, or network problems. Try uploading smaller files or different formats. Check your internet connection and try again."
      },
      {
        question: "The app is running slowly. How can I fix this?",
        answer: "Try closing other apps, clearing your browser cache, or restarting your device. Make sure you have the latest version of the app. If the issue persists, it might be due to server load or network issues."
      }
    ],
    'billing': [
      {
        question: "What are the different pricing plans?",
        answer: "We offer a free plan with basic features, a Pro plan ($9.99/month) with advanced features, and a Business plan ($19.99/month) for teams. All plans include core messaging features, with premium plans adding advanced functionality."
      },
      {
        question: "How do I upgrade my plan?",
        answer: "To upgrade, go to Account Settings > Billing & Plans, select your desired plan, and follow the payment process. You can upgrade anytime and your new features will be available immediately."
      },
      {
        question: "Can I cancel my subscription anytime?",
        answer: "Yes, you can cancel your subscription at any time from your account settings. Your premium features will remain active until the end of your current billing period, then you'll revert to the free plan."
      },
      {
        question: "Do you offer refunds?",
        answer: "We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied with your subscription, contact our support team within 30 days of purchase for a full refund."
      }
    ],
    'technical': [
      {
        question: "What are the system requirements?",
        answer: "For web: Any modern browser (Chrome, Firefox, Safari, Edge). For mobile: iOS 12+ or Android 8+. For desktop: Windows 10+, macOS 10.14+, or Linux. We recommend at least 4GB RAM and a stable internet connection."
      },
      {
        question: "How do I update the app?",
        answer: "Mobile apps update automatically through your device's app store. For desktop apps, updates are automatic or you can check for updates in the app menu. Web users always get the latest version automatically."
      },
      {
        question: "Can I use WeMeetTo offline?",
        answer: "WeMeetTo requires an internet connection to send and receive messages. However, you can view previously loaded conversations offline. Messages will sync when you reconnect to the internet."
      },
      {
        question: "How do I contact technical support?",
        answer: "You can contact our technical support team through the in-app support feature, email us at support@wemeetto.com, or use our live chat during business hours. We typically respond within 24 hours."
      }
    ]
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const toggleFAQ = (sectionId, itemIndex) => {
    const key = `${sectionId}-${itemIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleScroll = () => {
    const scrollPosition = window.scrollY + 100;
    
    for (let i = sections.length - 1; i >= 0; i--) {
      const section = document.getElementById(sections[i].id);
      if (section && section.offsetTop <= scrollPosition) {
        setActiveSection(sections[i].id);
        break;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`faq-container ${isVisible ? 'visible' : ''}`}>
      <Navbar />
      {/* Hero Section */}
      <div className="faq-hero">
        <div className="faq-hero-content">
          <h1 className="faq-hero-title">
            <span className="faq-title-word">Frequently</span>
            <span className="faq-title-word">Asked</span>
            <span className="faq-title-word">Questions</span>
          </h1>
          <p className="faq-hero-subtitle">
            Find answers to common questions about WeMeetTo
          </p>
          <div className="faq-hero-decoration">
            <div className="faq-decoration-circle"></div>
            <div className="faq-decoration-line"></div>
            <div className="faq-decoration-circle"></div>
          </div>
        </div>
      </div>

      <div className="faq-content-wrapper">
        {/* Table of Contents */}
        <div className="faq-table-of-contents">
          <h3>Categories</h3>
          <nav className="faq-toc-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`faq-toc-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="faq-content">
          <div className="faq-content-card">
            {sections.map((section) => (
              <section key={section.id} id={section.id} className="faq-section">
                <h2 className="faq-section-title">
                  <span className="faq-section-number">{String(sections.indexOf(section) + 1).padStart(2, '0')}</span>
                  {section.title}
                </h2>
                <div className="faq-section-content">
                  {faqData[section.id].map((faq, index) => {
                    const isOpen = openItems[`${section.id}-${index}`];
                    return (
                      <div key={index} className="faq-item">
                        <button
                          className={`faq-question ${isOpen ? 'open' : ''}`}
                          onClick={() => toggleFAQ(section.id, index)}
                        >
                          <span className="faq-question-text">{faq.question}</span>
                          <span className="faq-icon">
                            <svg
                              className={`faq-chevron ${isOpen ? 'rotated' : ''}`}
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="6,9 12,15 18,9"></polyline>
                            </svg>
                          </span>
                        </button>
                        <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                          <div className="faq-answer-content">
                            <p>{faq.answer}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            {/* Contact Support Section */}
            <section className="faq-support-section">
              <div className="faq-support-card">
                <div className="faq-support-icon">💬</div>
                <h3>Still have questions?</h3>
                <p>Can't find what you're looking for? Our support team is here to help!</p>
                <div className="faq-support-buttons">
                  <button className="faq-support-btn primary">
                    Contact Support
                  </button>
                  <button className="faq-support-btn secondary">
                    Live Chat
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      {/* <div className="faq-footer">
        <div className="faq-footer-content">
          <p>© 2024 WeMeetTo. All rights reserved.</p>
          <div className="faq-footer-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="/faq">FAQ</a>
          </div>
        </div>
      </div> */}
      <Footer/>
    </div>
  );
};

export default FAQ;
